/**
 * Салбарын самбарын дөрвөн KPI — өнөөдөр, энэ сар, нөөц, агуулахаас.
 *
 * ⚠️ Орлого нь ЗАРСАН ҮНЭ (хөнгөлөлт хассан, буцаалт, VOID ороогүй) —
 *    `/reports/sales`-тэй ижил цэвэр дүн. Өртөг, ашиг энд БАЙХГҮЙ.
 *
 * ⚠️ Сарын харьцуулалт өмнөх сарын МӨН ҮЕТЭЙ (шийдвэр 2026-09-18):
 *    сарын 10-нд бүтэн өнгөрсөн сартай харьцуулбал үргэлж уналт гарна.
 */

import { changePercent, formatMnt, formatMntShort, formatQty } from '@boots/domain';
import type { BranchDashboardData } from '../../lib/types';
import { StatCard, StatGrid } from '../stat-card';

export function BranchKpis({ d }: { d: BranchDashboardData }) {
  const { today, month, prev } = d.sales;
  const pct = changePercent(month.revenueMnt, prev.revenueMnt);
  const transit = d.inTransit.reduce((n, t) => n + t.count, 0);

  return (
    <StatGrid>
      <StatCard
        label="Өнөөдрийн орлого"
        icon="receipt"
        value={formatMntShort(today.revenueMnt)}
        delta={{ text: `${formatQty(today.units)} ш зарагдсан`, good: today.units > 0 ? true : undefined }}
        to="/orders"
      />

      <StatCard
        label="Энэ сарын орлого"
        icon="chart"
        value={formatMntShort(month.revenueMnt)}
        delta={{
          text:
            pct === null
              ? `${formatQty(month.units)} ш`
              : `${formatQty(month.units)} ш · өмнөх сарын мөн үеэс ${Math.abs(pct)}%`,
          direction: pct === null ? undefined : pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat',
          good: pct === null ? undefined : pct >= 0,
        }}
        meter={d.target ? Math.min(100, d.target.pct) : undefined}
        footer={
          d.target ? (
            <span className="num text-micro text-ink-3">
              Зорилт {formatMntShort(d.target.targetMnt)} · {d.target.pct}% биелсэн
            </span>
          ) : null
        }
        to="/orders"
        info={
          <>
            Сарын эхнээс өнөөдрийг өмнөх сарын <b>яг ижил урттай</b> хугацаатай
            харьцуулав — тэр үед {formatQty(prev.units)} ш, {formatMnt(prev.revenueMnt)}.{' '}
            {d.target
              ? <>Энэ сарын зорилт <b>{formatMnt(d.target.targetMnt)}</b>.</>
              : 'Энэ сард зорилт тавигдаагүй.'}{' '}
            Буцаагдсан, хүчингүй болсон захиалга ороогүй цэвэр дүн.
          </>
        }
      />

      <StatCard
        label="Салбарт байгаа"
        icon="store"
        value={formatQty(d.stock.onHand)}
        delta={
          d.stock.zeroPriced > 0
            ? { text: `${formatQty(d.stock.zeroPriced)} гутал 0₮ үнэтэй`, good: false }
            : { text: `лангуун дээр ${formatQty(d.stock.onFloor)} ш` }
        }
        info={
          <>
            Лангуу ба агуулах өрөөнд байгаа зарагдах гутал. Үүнээс{' '}
            <b>{formatQty(d.stock.onFloor)}</b> нь лангуун дээр.
            {d.stock.zeroPriced > 0
              ? ' 0₮ үнэтэй гутал касс дээр уншигдахгүй — агуулахад үнэ тавиулна уу.'
              : null}
          </>
        }
      />

      <StatCard
        label="Агуулахаас ирсэн"
        icon="truck"
        value={formatQty(d.pulled.month)}
        delta={{ text: transit > 0 ? `замд ${formatQty(transit)} ш ирж яваа` : 'энэ сард хүлээн авсан' }}
        info="Агуулахаас шилжүүлгээр энэ сард хүлээн авсан гутал. Шилжүүлэг хаагдах мөчид тоологдоно."
      />
    </StatGrid>
  );
}
